import { development, portalServerUrls } from "../config";
import { useSettingsStore } from "../data/settings/store";

export type PortalApiConfig = {
  readonly type: "legacy" | "dev" | "new" | "custom";
  /** Whether the currently selected portal server option targets the new API. */
  readonly useNewApi: boolean;
  /**
   * Base URL to use for new-API requests. `undefined` when the "custom"
   * option is selected but no URL has been configured yet — callers must
   * treat this as "not ready" rather than falling back to the legacy host.
   */
  readonly baseUrl: string | undefined;
};

/**
 * Resolves the portal server configuration shared by all sync modules that
 * can talk to the new API. Centralizes the useNewApi/baseUrl calculation so
 * an unconfigured custom portal can never silently fall back to the legacy
 * endpoint.
 */
export function usePortalApi(): PortalApiConfig {
  const { portalServer: portalServerOption, customPortalServerUrl } =
    useSettingsStore((s) => s.settings);

  const useNewApi =
    portalServerOption === "dev" ||
    portalServerOption === "new" ||
    portalServerOption === "custom";

  const customBaseUrl =
    customPortalServerUrl && isAllowedCustomPortalUrl(customPortalServerUrl)
      ? customPortalServerUrl
      : undefined;

  const baseUrl =
    portalServerOption === "custom"
      ? customBaseUrl
      : (portalServerUrls[
          portalServerOption as keyof typeof portalServerUrls
        ] ?? portalServerUrls.legacy);

  return { type: portalServerOption, useNewApi, baseUrl };
}

/**
 * Custom portal URLs carry the device's Authorization bearer token, so we
 * require HTTPS by default. Plain HTTP is only allowed during local
 * development (e.g. testing against a portal server on localhost).
 */
function isAllowedCustomPortalUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    if (parsed.protocol === "https:") return true;
    return development && parsed.protocol === "http:";
  } catch {
    return false;
  }
}
